import { useCallback, useMemo, useState } from 'react';
import { startCase } from 'lodash';
import PropTypes from 'prop-types';

import { Box, Collapse, Grid, Typography } from '@material-ui/core';

import { Accordion, FormText } from 'components';

import * as utils from 'utils';

import { useStyles } from './EditLayersRiskQuote.styles';

const prettify = (value) =>
  `${utils.string.t('format.number', {
    value: { number: value, default: '-' },
  })}`;

export const LayerPremium = ({ layerPremium, control, watch, formErrors, resetError, layerFieldsDefinitions, isEndorsement }) => {
  const classes = useStyles();

  const fields = Object.keys(layerPremium.premiums).map((premium) => ({
    id: premium,
    name: premium,
    value: layerPremium.premiums[premium],
    type: 'number',
    label: layerFieldsDefinitions.find((def) => def.name === premium)?.label ?? startCase(premium),
  }));

  const [expanded, setExpanded] = useState(false);

  const layerPremiumValue = watch(`${layerPremium.name}`);

  const handleAccordion = (_, isExpanded) => {
    setExpanded(isExpanded);
  };

  const getValue = useCallback((value, field) => {
    if (value === '') {
      return '-';
    }

    if (!value) {
      return prettify(field.value);
    }

    const formattedValue = parseFloat(`${value}`);

    if (formattedValue === field.value) {
      return prettify(field.value);
    }

    return `${prettify(formattedValue)}*`;
  }, []);

  const title = useMemo(
    () => (
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
        <Box className={classes.layerPremiumTitleWrapper}>
          <Typography variant="h4" className={classes.layerPremiumTitle}>
            {layerPremium.name}
          </Typography>
        </Box>
        <Box className={classes.layerPremiumDetails}>
          <Collapse in={!expanded}>
            <Box>
              {fields.map((field) => (
                <Typography variant="body2" className={classes.layerPremiumDetails} key={field.id}>
                  {field.label}: {getValue(layerPremiumValue?.[field.name], field)}
                </Typography>
              ))}
            </Box>
          </Collapse>
        </Box>
      </Box>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expanded, layerPremiumValue, getValue]
  );

  return (
    <Grid item xs={12}>
      <Accordion icon expanded={expanded} titleComponent={title} onChange={handleAccordion}>
        <Grid container spacing={2}>
          {fields.map((field) => {
            const error = formErrors?.[`${layerPremium.name}.${field.name}`];

            return (
              <Grid item xs={12} md={6} key={field.id}>
                <FormText
                  name={`${layerPremium.name}.${field.name}`}
                  control={control}
                  label={field.label}
                  defaultValue={field.value ?? ''}
                  type="number"
                  muiComponentProps={{
                    isAllowed: ({ floatValue }) => (isEndorsement ? true : !floatValue || floatValue >= 0),
                    onChange: () => resetError(`${layerPremium.name}.${field.name}`),
                  }}
                  error={error && { message: error }}
                  rules={{ required: 'Required' }}
                />
              </Grid>
            );
          })}
        </Grid>
      </Accordion>
    </Grid>
  );
};

LayerPremium.propTypes = {
  layerPremium: PropTypes.shape({
    name: PropTypes.string.isRequired,
    premiums: PropTypes.shape({
      [PropTypes.string]: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  control: PropTypes.shape().isRequired,
  watch: PropTypes.func.isRequired,
  formErrors: PropTypes.shape({
    [PropTypes.string]: PropTypes.string,
  }).isRequired,
  resetError: PropTypes.func.isRequired,
  layerFieldsDefinitions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  isEndorsement: PropTypes.bool,
};
